import { Injectable, Type } from "@angular/core";
import { array, object, record, string, unknown } from "zod";

import {
  AlertsListWidget,
  FactsWidget,
  OrdersTableWidget,
  PnlSummaryWidget,
  PositionsTableWidget,
  RiskMetricsWidget,
} from "./widgets/agent-widgets";

/**
 * M2-1 / F2·m3 — generative-UI component catalog.
 *
 * Maps a FIXED allowlist of component `type` strings to presentational, read-only
 * widget components. When the agent calls the `show_components` client tool, its
 * `{ components: [{type, props}, …] }` payload is validated here. Every entry must
 * use an allowlisted type and a record-shaped props bag; otherwise the caller
 * rejects the whole batch. Diagnostic fallback entries are returned for logging
 * and tests, but are never partially presented beside valid widgets. The catalog
 * is the single source of truth for what the agent is allowed to draw on screen.
 *
 * SECURITY: the allowlist is the trust boundary. An agent can only ever mount one
 * of these vetted components — never arbitrary markup — and each widget renders
 * solely via Angular interpolation / safe bindings (no innerHTML / no
 * bypassSecurityTrust*). Props are passed through verbatim for the widget to
 * Zod-parse defensively on its own.
 */

/** One validated `show_components` entry, resolved against the allowlist. */
export interface CatalogEntry {
  /** The requested component type (as supplied by the agent). */
  readonly type: string;
  /**
   * The resolved standalone widget component, or `null` when the type is not in
   * the allowlist / the entry was malformed — a `null` component renders as the
   * safe fallback text row.
   */
  readonly component: Type<unknown> | null;
  /** The agent-supplied props bag, forwarded to the widget's `props` input. */
  readonly props: Record<string, unknown>;
}

/** Result of validating a whole `show_components` payload. */
export interface CatalogValidation {
  /** Entries in the agent-supplied order (rendered widgets AND fallback rows). */
  readonly entries: readonly CatalogEntry[];
  /** Count of entries that resolved to a real widget (the ack's `rendered`). */
  readonly rendered: number;
  /** Types that fell back (unknown type or malformed entry) — the ack's `unknownTypes`. */
  readonly unknownTypes: readonly string[];
  /** Whether any part of the payload was invalid, requiring whole-batch rejection. */
  readonly rejected: boolean;
}

/** A single agent-supplied component request: `{ type, props }`. */
const AgentComponentSchema = object({
  type: string().min(1),
  props: record(string(), unknown()).optional(),
});

/** The `show_components` tool args: `{ components: [...] }`. Entries stay `unknown`
 * so validation can identify every malformed entry without executing anything. */
const ShowComponentsPayloadSchema = object({
  components: array(unknown()).min(1).max(20),
});

@Injectable({ providedIn: "root" })
export class AgentComponentCatalogService {
  /** The fixed type → widget allowlist. Order here is documentation only. */
  private readonly catalog: ReadonlyMap<string, Type<unknown>> = new Map<
    string,
    Type<unknown>
  >([
    ["positions-table", PositionsTableWidget],
    ["pnl-summary", PnlSummaryWidget],
    ["risk-metrics", RiskMetricsWidget],
    ["orders-table", OrdersTableWidget],
    ["alerts-list", AlertsListWidget],
    ["facts", FactsWidget],
  ]);

  /** True when `type` is an allowlisted, renderable widget. */
  has(type: string): boolean {
    return this.catalog.has(type);
  }

  /** The allowlisted component types (for the tool description / diagnostics). */
  allowedTypes(): readonly string[] {
    return [...this.catalog.keys()];
  }

  /**
   * Validate a raw `show_components` args object into an ordered list of catalog
   * entries. Unknown types and malformed entries resolve to diagnostic rows
   * (`component: null`) rather than throwing; `rejected` requires the caller to
   * reject the WHOLE batch so no partial untrusted payload is rendered.
   */
  validate(rawArgs: unknown): CatalogValidation {
    const payload = ShowComponentsPayloadSchema.safeParse(rawArgs);
    if (!payload.success) {
      return {
        entries: [],
        rendered: 0,
        unknownTypes: ["(invalid-payload)"],
        rejected: true,
      };
    }
    const list = payload.data.components;

    const entries: CatalogEntry[] = [];
    const unknownTypes: string[] = [];
    let rendered = 0;

    for (const raw of list) {
      const parsed = AgentComponentSchema.safeParse(raw);
      if (!parsed.success) {
        const type = extractType(raw);
        entries.push({ type, component: null, props: {} });
        unknownTypes.push(type);
        continue;
      }
      const { type } = parsed.data;
      const props = parsed.data.props ?? {};
      const component = this.catalog.get(type) ?? null;
      if (component === null) {
        entries.push({ type, component: null, props: {} });
        unknownTypes.push(type);
        continue;
      }
      entries.push({ type, component, props });
      rendered += 1;
    }

    return {
      entries,
      rendered,
      unknownTypes,
      rejected: unknownTypes.length > 0,
    };
  }
}

/** Best-effort type label for a malformed entry so the fallback row is meaningful. */
function extractType(raw: unknown): string {
  if (raw !== null && typeof raw === "object" && "type" in raw) {
    const t = (raw as { type: unknown }).type;
    if (typeof t === "string" && t.length > 0) {
      return t;
    }
  }
  return "(invalid)";
}
